import { createI18nSettings } from '@kit/i18n';

/**
 * The default language of the application.
 * This is used as a fallback language when the selected language is not supported.
 *
 */
const defaultLanguage = process.env.NEXT_PUBLIC_DEFAULT_LOCALE ?? 'en';

/**
 * The list of supported languages — the 15 most-used by Ballee dancers.
 *
 * Untranslated keys fall back to `defaultLanguage` (en) automatically
 * (createI18nSettings sets `fallbackLng`), so a locale only needs the keys it
 * has actually translated; the long tail renders English until filled. Keep
 * this list in sync with the mobile slang locales (apps/mobile/lib/i18n/
 * strings_<code>.i18n.json).
 *
 * `ar` is RTL — the document `dir` is derived from the active locale in
 * apps/web/app/layout.tsx via `isRtlLocale()` below.
 */
export const languages: string[] = [
  defaultLanguage, // en
  'fr',
  'es',
  'de',
  'it',
  'pt',
  'nl',
  'pl',
  'uk',
  'ru',
  'ja',
  'ko',
  'zh-CN',
  'ar',
  'tr',
];

/**
 * Right-to-left locales. Drives the `<html dir>` attribute. Today only Arabic;
 * add `he`, `fa`, `ur` here if those locales are introduced.
 */
const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur']);

/**
 * Is the given locale written right-to-left? Used to set `<html dir="rtl">`.
 */
export function isRtlLocale(locale: string | undefined): boolean {
  if (!locale) return false;
  // Match on the base subtag so `ar-EG` etc. resolve correctly.
  return RTL_LOCALES.has(locale.split('-')[0] ?? locale);
}

/**
 * The name of the cookie that stores the selected language.
 */
export const I18N_COOKIE_NAME = 'lang';

/**
 * The default array of Internationalization (i18n) namespaces.
 *
 * MUST list every namespace file present on disk under
 * public/locales/<locale>/. A namespace missing from this array is not loaded
 * by `getI18nSettings()` default, so its keys silently render raw. This list
 * was drifted (29 entries) while disk had 34 — `dancers`, `documents`,
 * `guardians`, `orders`, `plans`, `policies` existed but were never loaded.
 * Keep it complete; the parity script (scripts/i18n/check-parity.mjs) asserts
 * the array matches the EN namespace files on disk.
 **/
export const defaultI18nNamespaces = [
  'account',
  'admin',
  'agency',
  'analytics',
  'auditions',
  'auth',
  'billing',
  'booklet',
  'bookmarks',
  'chat',
  'claude-connection',
  'client',
  'clientUser',
  'coaching',
  'common',
  'community',
  'competitionApplication',
  'competitions',
  'contractDetails',
  'dancer',
  'dancers',
  'demo',
  'documents',
  'events',
  'explore',
  'feedback',
  'home',
  'invoices',
  'lists',
  'marketing',
  'merge',
  'offers',
  'onboarding',
  'orders',
  'organizations',
  'plans',
  'policies',
  'profiles',
  'referrals',
  'reimbursements',
  'search',
  'shop',
  'teams',
  'terms',
  'user',
];

/**
 * Get the i18n settings for the given language and namespaces.
 * If the language is not supported, it will fall back to the default language.
 * @param language
 * @param ns
 */
export function getI18nSettings(
  language: string | undefined,
  ns: string | string[] = defaultI18nNamespaces,
) {
  let lng = language ?? defaultLanguage;

  if (!languages.includes(lng)) {
    console.warn(
      `Language "${lng}" is not supported. Falling back to "${defaultLanguage}"`,
    );

    lng = defaultLanguage;
  }

  return createI18nSettings({
    language: lng,
    namespaces: ns,
    languages,
  });
}
